import React from 'react';

import { getStaticsBaseUrl } from '@oysterjs/core/config';

import type { RenderedBoundary } from '~/components/document-editor/extensions/page-guide';
import { fetchWithRenderGrant } from '~/components/workspaces/lib/render-grant';

/** How many page breaks a saved document carries. */
export const countPageBreaks = (html: string): number =>
  (html.match(/<div\b[^>]*\bdata-page-break\b/gi) ?? []).length;

/**
 * Where the renderer started each page, as a sample of that page's opening text.
 *
 * Each read costs a full render in the pod, so this deliberately depends only on the saved
 * version, the style and the revision, never on the content being typed. Guides go stale
 * between saves and fall back to measurement rather than re-rendering on every keystroke; the
 * editor bumps the revision when a save changed the number of explicit page breaks.
 */
export function usePageBreaks(params: {
  enabled: boolean;
  workspaceId?: string;
  artifactId?: string;
  artifactVersion?: number;
  templateId?: string;
  revision?: string | number;
}) {
  const { enabled, workspaceId, artifactId, artifactVersion, templateId, revision } = params;
  const url = React.useMemo(() => {
    if (!workspaceId || !artifactId || artifactVersion === undefined) return null;
    const query = new URLSearchParams({ version: String(artifactVersion) });
    // Older statics reads the query style during mixed deployments.
    if (templateId) query.set('style_id', templateId);
    if (revision !== undefined) query.set('render_revision', String(revision));

    return `${getStaticsBaseUrl()}/agency/html/${workspaceId}/${artifactId}/page-breaks?${query.toString()}`;
  }, [workspaceId, artifactId, artifactVersion, revision, templateId]);
  const requestKey = url === null ? null : JSON.stringify([url, templateId ?? null]);
  const [state, setState] = React.useState<{
    requestKey: string | null;
    boundaries: RenderedBoundary[] | null;
    pageCount: number;
    declaredPageCount: number;
  }>({ requestKey: null, boundaries: null, pageCount: 0, declaredPageCount: 0 });

  React.useEffect(() => {
    if (!enabled || !requestKey || state.requestKey === requestKey) return;
    if (!url || !workspaceId || !artifactId || artifactVersion === undefined) return;
    const controller = new AbortController();
    // Statics answers only with a grant for this version. A refusal leaves no boundaries, and
    // the editor quietly falls back to drawing the pages the document claims rather than the
    // ones it has.
    fetchWithRenderGrant(
      url,
      { workspaceId, artifactId, version: artifactVersion, styleId: templateId },
      controller.signal
    )
      .then((response) => (response.ok ? response.json() : null))
      .then((body) => {
        if (controller.signal.aborted) return;
        setState({
          requestKey,
          boundaries: Array.isArray(body?.boundaries) ? body.boundaries : null,
          pageCount: typeof body?.pageCount === 'number' ? body.pageCount : 0,
          declaredPageCount:
            typeof body?.declaredPageCount === 'number' ? body.declaredPageCount : 0
        });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setState({ requestKey, boundaries: null, pageCount: 0, declaredPageCount: 0 });
      });

    return () => controller.abort();
  }, [
    enabled,
    requestKey,
    state.requestKey,
    url,
    workspaceId,
    artifactId,
    artifactVersion,
    templateId
  ]);

  return enabled && requestKey !== null && state.requestKey === requestKey
    ? {
        boundaries: state.boundaries,
        pageCount: state.pageCount,
        declaredPageCount: state.declaredPageCount
      }
    : { boundaries: null, pageCount: 0, declaredPageCount: 0 };
}
